/**
 * Display labels for the loan enums the lending-preview pages print.
 *
 * The pages printed these codes raw or through `code.replace(/_/g, ' ')`, so an
 * FI operator read "SECURITIES_BACKED" or "SECURITIES BACKED". Each map is keyed
 * by the generated spec enum, so a new enum value fails tsc here until it has a
 * label:
 *   - loan type: LoanDetails.loanType, loan-syndication-protocol.openapi.yaml
 *   - loan status: LoanViewItem.loanStatus, admin-portal-bff.openapi.yaml
 *   - collateral type: CollateralType, loan-origination.openapi.yaml. The Active
 *     Loans collateral column (LoanViewItem.collateralType) carries the
 *     package's collateral type when the package declares one and the loan
 *     type otherwise, so collateralLabel reads both vocabularies.
 *
 * A code outside the enum (the loan type column is a free string in the
 * member and portfolio responses) prints as received, so an unmapped value is
 * visible rather than hidden behind a guessed label.
 */

import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import type { components as LoanOriginationComponents } from '@aaimplatform/institutional-types/loan-origination';
import type { AdminPortalBff } from '@aaimplatform/institutional-types';
import type { BadgeProps } from '@aaimplatform/ui';

type LoanTypeCode = LspComponents['schemas']['LoanDetails']['loanType'];
type CollateralTypeCode = LoanOriginationComponents['schemas']['CollateralType'];
export type LoanStatusCode = NonNullable<AdminPortalBff.components['schemas']['LoanViewItem']['loanStatus']>;

const LOAN_TYPE_LABELS: Record<LoanTypeCode, string> = {
  PLEDGED_ASSET: 'Pledged asset',
  SECURITIES_BACKED: 'Securities-backed',
  DIGITAL_ASSET_BACKED: 'Digital-asset-backed',
  OVERDRAFT_RECEIVABLES_POOL: 'Overdraft receivables pool',
};

const COLLATERAL_TYPE_LABELS: Record<CollateralTypeCode, string> = {
  real_estate: 'Real estate',
  vehicle: 'Vehicle',
  securities: 'Securities',
  cash: 'Cash',
  equipment: 'Equipment',
  other: 'Other',
};

const LOAN_STATUS_LABELS: Record<LoanStatusCode, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  UNDERWRITING: 'Underwriting',
  APPROVED: 'Approved',
  FUNDED: 'Funded',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  CLOSED: 'Closed',
  DEFAULTED: 'Defaulted',
};

/**
 * Badge variant per loan status. The value type is the kit Badge's own
 * `variant` prop, so a variant the badge drops fails tsc here. Badge is the
 * one status chip (UI run r3, status-chip-primitive); it prints the label in
 * sentence case, as registered above.
 */
const LOAN_STATUS_TONES: Record<LoanStatusCode, NonNullable<BadgeProps['variant']>> = {
  DRAFT: 'warning',
  SUBMITTED: 'warning',
  UNDERWRITING: 'warning',
  APPROVED: 'success',
  FUNDED: 'success',
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  CLOSED: 'neutral',
  DEFAULTED: 'error',
};

function isKey<T extends string>(map: Record<T, unknown>, code: string): code is T {
  return Object.prototype.hasOwnProperty.call(map, code);
}

/** Label for a loan type code; an unmapped code prints as received. */
export function loanTypeLabel(code: string | null | undefined): string {
  if (!code) return '--';
  return isKey(LOAN_TYPE_LABELS, code) ? LOAN_TYPE_LABELS[code] : code;
}

/**
 * Label for LoanViewItem.collateralType: a package collateral type, else a loan
 * type. An unmapped code prints as received.
 */
export function collateralLabel(code: string | null | undefined): string {
  if (!code) return '--';
  if (isKey(COLLATERAL_TYPE_LABELS, code)) return COLLATERAL_TYPE_LABELS[code];
  return loanTypeLabel(code);
}

/** Label for a LoanStatus code; an unmapped code prints as received. */
export function loanStatusLabel(code: string | null | undefined): string {
  if (!code) return '--';
  return isKey(LOAN_STATUS_LABELS, code) ? LOAN_STATUS_LABELS[code] : code;
}

/** Badge variant for a LoanStatus code; unmapped codes read as neutral. */
export function loanStatusTone(
  code: string | null | undefined,
): NonNullable<BadgeProps['variant']> {
  if (!code) return 'neutral';
  return isKey(LOAN_STATUS_TONES, code) ? LOAN_STATUS_TONES[code] : 'neutral';
}
